import React, { useEffect, useMemo, useState } from 'react';
import { Search, ChevronRight, X } from 'lucide-react';
import { thiCases, searchCases } from '../../../data/hospitals/thiCases';

/*
 * Case Setup Guide body: search across every case, wrapped procedure chips for
 * the active category, and the selected procedure's card(s). The category
 * itself is chosen by the segmented control above (HospitalDetailScreen).
 */

const procKey = (categoryId) => `case-setup-proc:${categoryId}`;

const ItemText = ({ item, onAction }) =>
  typeof item === 'string' ? (
    <>{item}</>
  ) : (
    <button
      type="button"
      onClick={() => { onAction?.(item.action); }}
      className="font-medium text-gray-900 dark:text-white inline-flex items-center gap-0.5 text-left"
    >
      {item.text}
      <ChevronRight size={15} className="text-gray-400" />
    </button>
  );

export const CaseCard = ({ card, onAction }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
    <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700/70">
      <h3 className="font-bold text-xl text-gray-900 dark:text-white">{card.title}</h3>
    </div>
    <div className="p-6 space-y-4">
      {card.sections.map((section) => (
        <div key={section.heading}>
          <h5 className="font-semibold text-gray-900 dark:text-white mb-2">{section.heading}</h5>
          {section.items.length === 1 && typeof section.items[0] === 'string' ? (
            <p className="text-gray-700 dark:text-gray-300">{section.items[0]}</p>
          ) : (
            <ul className="space-y-1">
              {section.items.map((item, i) => (
                <li key={i} className="text-gray-700 dark:text-gray-300">
                  <ItemText item={item} onAction={onAction} />
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  </div>
);

const ComingSoonCard = ({ procedure }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-card p-6 text-center">
    <p className="text-lg font-semibold text-gray-900 dark:text-white">{procedure.title}</p>
    <p className="text-sm text-gray-400 dark:text-gray-500 mt-1 italic">Setup details coming soon</p>
    {procedure.note && <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{procedure.note}</p>}
  </div>
);

const CaseSetup = ({ category, onCategoryChange, onAction, extras }) => {
  const [query, setQuery] = useState('');
  const cat = thiCases.find((c) => c.id === category) || thiCases[0];

  // Selected procedure per category, remembered on the device
  const [procedureId, setProcedureId] = useState(() => {
    try { return localStorage.getItem(procKey(cat.id)) || cat.procedures[0].id; } catch { return cat.procedures[0].id; }
  });
  useEffect(() => {
    let saved = null;
    try { saved = localStorage.getItem(procKey(cat.id)); } catch { /* ignore */ }
    setProcedureId(cat.procedures.some((p) => p.id === saved) ? saved : cat.procedures[0].id);
  }, [cat]);
  const selectProcedure = (id) => {
    setProcedureId(id);
    try { localStorage.setItem(procKey(cat.id), id); } catch { /* ignore */ }
  };

  const procedure = cat.procedures.find((p) => p.id === procedureId) || cat.procedures[0];
  const results = useMemo(() => searchCases(query), [query]);

  const jumpTo = (p) => {
    setQuery('');
    if (p.categoryId !== cat.id) onCategoryChange?.(p.categoryId);
    try { localStorage.setItem(procKey(p.categoryId), p.id); } catch { /* ignore */ }
    setProcedureId(p.id);
  };

  return (
    <div className="space-y-4">
      {/* Search every case type */}
      <div className="relative">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Find a case"
          className="w-full pl-10 pr-10 py-2.5 text-[15px] rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600"
        />
        {query && (
          <button onClick={() => setQuery('')} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
            <X size={16} />
          </button>
        )}
      </div>

      {query.trim().length >= 2 ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/70">
          {results.length === 0 && (
            <div className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">No cases match "{query.trim()}"</div>
          )}
          {results.map(({ procedure: p, title, snippet }) => (
            <button key={`${p.categoryId}/${p.id}`} onClick={() => jumpTo(p)} className="w-full text-left px-4 py-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-medium text-gray-900 dark:text-white leading-snug">{title}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                  {p.categoryLabel}{snippet ? ` · ${snippet}` : ''}{p.comingSoon ? ' · coming soon' : ''}
                </div>
              </div>
              <ChevronRight size={16} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
            </button>
          ))}
        </div>
      ) : (
        <>
          {/* Procedures in this category, wrapping so none are hidden */}
          <div className="flex flex-wrap gap-2" role="tablist">
            {cat.procedures.map((p) => {
              const active = p.id === procedure.id;
              return (
                <button
                  key={p.id}
                  role="tab"
                  aria-selected={active}
                  onClick={() => { selectProcedure(p.id); }}
                  className={`px-3.5 py-2 rounded-full text-xs font-semibold transition-all ${
                    active
                      ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900 shadow-sm'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          {procedure.comingSoon
            ? <ComingSoonCard procedure={procedure} />
            : procedure.cards.map((card) => <CaseCard key={card.title} card={card} onAction={onAction} />)}

          {extras}
        </>
      )}
    </div>
  );
};

export default CaseSetup;
